import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { RequestFormSelectField } from '../../components/RequestFormSelectField';
import {
  SearchableEntitySelector,
  type SelectorOption,
} from '../../components/SearchableEntitySelector';
import { SearchableMultiSelect } from '../../components/SearchableMultiSelect';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { normalizeApiError } from '../../api/apiError';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { getDepartments } from '../../services/departmentService';
import { getDesignations } from '../../services/designationService';
import {
  getAssignableRoles,
  getEmployeeDetails,
  getEmployees,
  getEmployeeWfhEligibility,
  updateEmployee,
} from '../../services/employeeService';
import { getLocations } from '../../services/locationService';
import type {
  EmployeeDetails,
  EmployeeWfhEligibility,
  WfhPolicyOption,
} from '../../types/employee';
import { ROLE_HR_ADMIN } from '../../utils/approvalRoles';
import { activeConfigurationFilters, isActiveConfigurationRecord } from '../../utils/configurationFilters';
import { filterRolesForEmployeeEdit } from '../../utils/employeeCreateRoles';
import { notifyEmployeesChanged } from '../../utils/employeeListSync';
import { runOperation } from '../../utils/operationFeedback';
import { EmployeeLeavePoliciesCard } from './EmployeeLeavePoliciesCard';

type Props = NativeStackScreenProps<RootStackParamList, 'EmployeeEdit'>;

function wfhPolicyLabel(policy: WfhPolicyOption): string {
  return policy.isDefault ? `${policy.policyName} (Default)` : policy.policyName;
}

function wfhPolicySummary(policy: WfhPolicyOption): string {
  const parts = [
    policy.maximumDaysPerMonth !== null
      ? `Up to ${policy.maximumDaysPerMonth} days per month`
      : 'No monthly limit',
    policy.allowHalfDay ? 'Half day allowed' : 'Full day only',
  ];
  return parts.join(' · ');
}

/** Policy pre-selected when WFH is on: the current one, else the org default. */
function initialWfhPolicyId(info: EmployeeWfhEligibility): string {
  const policy =
    (info.wfhEligible ? info.assignedPolicy : null) ?? info.defaultPolicy;
  return policy ? String(policy.wfhPolicyId) : '';
}

export function EmployeeEditScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  const employeeId = route.params.employeeId;
  const isSelf = auth.user?.employeeId === employeeId;
  // WFH and leave policy assignments: HR Admin only, never their own.
  const canManageWfh = auth.hasRole(ROLE_HR_ADMIN) && !isSelf;
  const [employee, setEmployee] = useState<EmployeeDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const [department, setDepartment] = useState<SelectorOption | null>(null);
  const [designation, setDesignation] = useState<SelectorOption | null>(null);
  const [location, setLocation] = useState<SelectorOption | null>(null);
  const [reportingManager, setReportingManager] = useState<SelectorOption | null>(
    null,
  );
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [officialEmail, setOfficialEmail] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [statusId, setStatusId] = useState('1');
  const [attendanceRequired, setAttendanceRequired] = useState(true);
  const [faceAttendanceEnabled, setFaceAttendanceEnabled] = useState(true);
  const [wfhEligible, setWfhEligible] = useState(false);
  const [wfhInfo, setWfhInfo] = useState<EmployeeWfhEligibility | null>(null);
  const [wfhInfoError, setWfhInfoError] = useState<string | null>(null);
  const [wfhPolicyId, setWfhPolicyId] = useState('');
  const [roles, setRoles] = useState<SelectorOption[]>([]);
  const [assignableRoleIds, setAssignableRoleIds] = useState<number[] | null>(
    null,
  );

  const themed = useMemo(
    () => ({
      error: { ...typography.body, color: palette.error },
      label: { ...typography.caption, color: palette.textMuted },
      sectionTitle: { ...typography.body, color: palette.textPrimary },
      hint: { ...typography.caption, color: palette.textMuted },
      warning: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  const applyEmployee = useCallback((details: EmployeeDetails) => {
    setEmployee(details);
    setFirstName(details.firstName ?? '');
    setMiddleName(details.middleName ?? '');
    setLastName(details.lastName ?? '');
    setOfficialEmail(details.officialEmail ?? '');
    setPersonalEmail(details.personalEmail ?? '');
    setMobileNumber(details.mobileNumber ?? '');
    setEmploymentType(details.employmentType?.trim() || 'FULL_TIME');
    setStatusId(String(details.statusId ?? 1));
    setAttendanceRequired(details.attendanceRequired !== false);
    setFaceAttendanceEnabled(details.faceAttendanceEnabled !== false);
    setWfhEligible(details.wfhEligible === true);
    setDepartment(
      details.departmentId
        ? {
            id: details.departmentId,
            label: details.department ?? `Department ${details.departmentId}`,
          }
        : null,
    );
    setDesignation(
      details.designationId
        ? {
            id: details.designationId,
            label:
              details.designation ?? `Designation ${details.designationId}`,
          }
        : null,
    );
    setLocation(
      details.primaryLocationId
        ? {
            id: details.primaryLocationId,
            label:
              details.primaryLocation ??
              `Location ${details.primaryLocationId}`,
          }
        : null,
    );
    setReportingManager(
      details.reportingManagerId
        ? {
            id: details.reportingManagerId,
            label:
              details.reportingManager ??
              `Manager ${details.reportingManagerId}`,
          }
        : null,
    );
    setRoles(
      (details.roles ?? []).map(role => ({
        id: role.roleId,
        label: role.roleName,
      })),
    );
  }, []);

  const loadWfh = useCallback(async () => {
    if (!canManageWfh) return;
    setWfhInfoError(null);
    try {
      const info = await getEmployeeWfhEligibility(employeeId);
      setWfhInfo(info);
      setWfhEligible(info.wfhEligible);
      setWfhPolicyId(initialWfhPolicyId(info));
    } catch (caught) {
      setWfhInfo(null);
      setWfhInfoError(
        normalizeApiError(caught).message || 'Unable to load WFH eligibility.',
      );
    }
  }, [canManageWfh, employeeId]);

  const loadAssignableRoleIds = useCallback(async () => {
    try {
      const items = await getAssignableRoles();
      setAssignableRoleIds(items.map(role => role.roleId));
    } catch {
      setAssignableRoleIds(null);
    }
  }, []);

  const lockedRoleIds = assignableRoleIds
    ? roles
        .filter(role => !assignableRoleIds.includes(role.id))
        .map(role => role.id)
    : [];

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const details = await getEmployeeDetails(employeeId);
      applyEmployee(details);
      await Promise.all([loadWfh(), loadAssignableRoleIds()]);
    } catch (caught) {
      setLoadError(
        normalizeApiError(caught).message || 'Unable to load employee.',
      );
    } finally {
      setLoading(false);
    }
  }, [applyEmployee, employeeId, loadAssignableRoleIds, loadWfh]);

  const wfhPolicyOptions = useMemo(
    () =>
      (wfhInfo?.availablePolicies ?? []).map(policy => ({
        value: String(policy.wfhPolicyId),
        label: wfhPolicyLabel(policy),
      })),
    [wfhInfo],
  );
  const selectedWfhPolicy =
    wfhInfo?.availablePolicies.find(
      policy => String(policy.wfhPolicyId) === wfhPolicyId,
    ) ?? null;
  // Without loaded policies HR cannot see what would be assigned, so the switch stays locked.
  const wfhEditable = canManageWfh && wfhInfo !== null;

  function toggleWfhEligible(next: boolean) {
    setWfhEligible(next);
    if (next && !wfhPolicyId && wfhInfo) {
      setWfhPolicyId(initialWfhPolicyId(wfhInfo));
    }
  }

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  async function loadDepartments(search: string): Promise<SelectorOption[]> {
    const result = await getDepartments({
      page: 1,
      pageSize: 20,
      filters: activeConfigurationFilters({ search }),
    });
    return result.items.map(item => ({
      id: item.departmentId,
      label: item.departmentName,
      subtitle: item.departmentCode,
    }));
  }

  async function loadDesignations(search: string): Promise<SelectorOption[]> {
    const result = await getDesignations({
      page: 1,
      pageSize: 20,
      filters: activeConfigurationFilters({ search }),
    });
    return result.items.map(item => ({
      id: item.designationId,
      label: item.designationName,
      subtitle: item.designationCode,
    }));
  }

  async function loadLocations(search: string): Promise<SelectorOption[]> {
    const result = await getLocations({
      page: 1,
      pageSize: 20,
      filters: activeConfigurationFilters({ search }),
    });
    return result.items.map(item => ({
      id: item.locationId,
      label: item.locationName,
      subtitle: item.locationCode,
    }));
  }

  function loadRoles(search: string): Promise<SelectorOption[]> | SelectorOption[] {
    // Edit Employee omits Office Kiosk / Team Leader (by role_code), same as Create.
    const pending = getAssignableRoles(search, { purpose: 'edit' });
    const mapItems = (items: Awaited<typeof pending>): SelectorOption[] =>
      filterRolesForEmployeeEdit(items).map(role => ({
        id: role.roleId,
        label: role.roleName,
        subtitle: role.description,
      }));
    if (typeof pending.then === 'function') return pending.then(mapItems);
    return mapItems(pending);
  }

  async function loadManagers(search: string): Promise<SelectorOption[]> {
    const result = await getEmployees({
      page: 1,
      pageSize: 20,
      filters: { search },
    });
    return result.items
      .filter(item => item.employeeId !== employeeId)
      .filter(item => isActiveConfigurationRecord(item.isActive))
      .map(item => ({
        id: item.employeeId,
        label: item.displayName,
        subtitle: item.employeeNumber,
      }));
  }

  async function submit() {
    if (busy || !employee) return;
    setError('');
    const parsedStatusId = Number(statusId);
    if (!firstName.trim() || !officialEmail.trim() || !employmentType.trim()) {
      setError('First name, official email, and employment type are required.');
      return;
    }
    if (!Number.isInteger(parsedStatusId) || parsedStatusId <= 0) {
      setError('Status ID must be a positive integer.');
      return;
    }
    if (roles.length === 0) {
      setError('Select at least one role.');
      return;
    }
    if (
      employee.rowVersion === null ||
      !Number.isInteger(employee.rowVersion) ||
      employee.rowVersion <= 0
    ) {
      setError('Employee version is missing. Reload and try again.');
      return;
    }
    const parsedWfhPolicyId = Number(wfhPolicyId);
    const sendWfhPolicy = wfhEditable && wfhEligible;
    if (sendWfhPolicy && !(parsedWfhPolicyId > 0)) {
      setError('Select a WFH policy for this employee.');
      return;
    }

    // Prefer the form selection; otherwise keep the loaded employee values so
    // we never clear org structure fields the API still requires.
    const departmentId = department?.id ?? employee.departmentId ?? null;
    const designationId = designation?.id ?? employee.designationId ?? null;
    const primaryLocationId =
      location?.id ?? employee.primaryLocationId ?? null;

    const rowVersion = employee.rowVersion;
    const dateOfBirth =
      employee.dateOfBirth && /^\d{4}-\d{2}-\d{2}$/.test(employee.dateOfBirth)
        ? employee.dateOfBirth
        : null;
    setBusy(true);
    try {
      const updated = await runOperation('update', () =>
        updateEmployee(employeeId, {
          departmentId,
          designationId,
          primaryLocationId,
          reportingManagerId: reportingManager?.id ?? null,
          firstName: firstName.trim(),
          middleName: middleName.trim() || null,
          lastName: lastName.trim() || null,
          officialEmail: officialEmail.trim(),
          personalEmail: personalEmail.trim() || null,
          mobileNumber: mobileNumber.trim() || null,
          dateOfBirth,
          genderCode: employee.genderCode,
          employmentType: employmentType.trim(),
          attendanceRequired,
          faceAttendanceEnabled,
          wfhEligible,
          ...(sendWfhPolicy ? { wfhPolicyId: parsedWfhPolicyId } : {}),
          statusId: parsedStatusId,
          rowVersion,
          roleIds: roles.map(role => role.id),
        }),
      );
      if (updated?.employeeId) applyEmployee(updated);
      notifyEmployeesChanged();
      navigation.goBack();
    } catch (caught) {
      setError(
        normalizeApiError(caught).message || 'Unable to update employee.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.EMPLOYEE_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !employee ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit employee"
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading employee" />
        </AppScreen>
      ) : loadError && !employee ? (
        <AppScreen webLayout="narrow" contentStyle={styles.center}>
          <ScreenHeader
            title="Edit employee"
            onBack={navigation.goBack}
          />
          <ErrorState message={loadError} onRetry={load} />
        </AppScreen>
      ) : (
        <AppScreen webLayout="narrow" scroll keyboardSafe>
          <ScreenHeader
            title="Edit employee"
            onBack={navigation.goBack}
          />
          <View style={styles.body}>
            <AppCard style={styles.card}>
              <SearchableEntitySelector
                label="Department (optional)"
                selectedValue={department?.id ?? null}
                selectedLabel={department?.label ?? null}
                loadOptions={loadDepartments}
                onSelect={setDepartment}
                onClear={() => setDepartment(null)}
              />
              <SearchableEntitySelector
                label="Designation (optional)"
                selectedValue={designation?.id ?? null}
                selectedLabel={designation?.label ?? null}
                loadOptions={loadDesignations}
                onSelect={setDesignation}
                onClear={() => setDesignation(null)}
              />
              <SearchableEntitySelector
                label="Primary location (optional)"
                selectedValue={location?.id ?? null}
                selectedLabel={location?.label ?? null}
                loadOptions={loadLocations}
                onSelect={setLocation}
                onClear={() => setLocation(null)}
              />
              <SearchableEntitySelector
                label="Reporting manager (required for team leads)"
                selectedValue={reportingManager?.id ?? null}
                selectedLabel={reportingManager?.label ?? null}
                loadOptions={loadManagers}
                onSelect={setReportingManager}
                onClear={() => setReportingManager(null)}
              />
              <AppInput
                label="First name"
                value={firstName}
                onChangeText={setFirstName}
              />
              <AppInput
                label="Middle name"
                value={middleName}
                onChangeText={setMiddleName}
              />
              <AppInput
                label="Last name"
                value={lastName}
                onChangeText={setLastName}
              />
              <AppInput
                label="Official email"
                value={officialEmail}
                onChangeText={setOfficialEmail}
                autoCapitalize="none"
                keyboardType="email-address"
              />
              <AppInput
                label="Personal email"
                value={personalEmail}
                onChangeText={setPersonalEmail}
                autoCapitalize="none"
                keyboardType="email-address"
              />
              <AppInput
                label="Mobile number"
                value={mobileNumber}
                onChangeText={setMobileNumber}
                keyboardType="phone-pad"
              />
              <AppInput
                label="Employment type"
                value={employmentType}
                onChangeText={setEmploymentType}
                autoCapitalize="characters"
              />
              <AppInput
                label="Status ID"
                value={statusId}
                onChangeText={setStatusId}
                keyboardType="number-pad"
              />
              <SearchableMultiSelect
                label="Roles"
                selected={roles}
                lockedIds={lockedRoleIds}
                loadOptions={loadRoles}
                onChange={setRoles}
              />
              <Text style={themed.hint}>
                Select one or more roles. The first role is the default role.
              </Text>
              <View style={styles.switchRow}>
                <Text style={themed.label}>Attendance required</Text>
                <Switch
                  value={attendanceRequired}
                  onValueChange={setAttendanceRequired}
                />
              </View>
              <View style={styles.switchRow}>
                <Text style={themed.label}>Face attendance</Text>
                <Switch
                  value={faceAttendanceEnabled}
                  onValueChange={setFaceAttendanceEnabled}
                />
              </View>
            </AppCard>
            <AppCard style={styles.card}>
              <Text style={themed.sectionTitle}>WFH eligibility</Text>
              <View style={styles.switchRow}>
                <Text style={themed.label}>WFH eligible</Text>
                <Switch
                  accessibilityLabel="WFH eligible"
                  value={wfhEligible}
                  onValueChange={toggleWfhEligible}
                  disabled={!wfhEditable}
                />
              </View>
              {!canManageWfh ? (
                <Text style={themed.hint}>
                  {isSelf
                    ? 'You cannot change your own WFH eligibility or policy.'
                    : 'Only HR Admin can change WFH eligibility and policy.'}
                </Text>
              ) : wfhInfoError ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    loadWfh().catch(() => undefined);
                  }}
                >
                  <Text style={themed.warning}>
                    {wfhInfoError} Tap to retry.
                  </Text>
                </Pressable>
              ) : wfhEligible && wfhInfo ? (
                <>
                  <RequestFormSelectField
                    label="Assigned WFH policy"
                    required
                    value={wfhPolicyId}
                    options={wfhPolicyOptions}
                    onChange={setWfhPolicyId}
                    placeholder="Select WFH policy"
                  />
                  {selectedWfhPolicy ? (
                    <Text style={themed.hint}>
                      {wfhPolicySummary(selectedWfhPolicy)}
                    </Text>
                  ) : null}
                  {!wfhInfo.wfhEligible && selectedWfhPolicy?.isDefault ? (
                    <Text style={themed.hint}>
                      The organization's default WFH policy is assigned when
                      you save. You can choose another policy.
                    </Text>
                  ) : null}
                  {!wfhInfo.defaultPolicy ? (
                    <Text style={themed.warning}>
                      No default WFH policy is configured. Select a policy or
                      mark one as default in WFH configuration.
                    </Text>
                  ) : null}
                </>
              ) : wfhInfo?.wfhEligible && !wfhEligible ? (
                <Text style={themed.hint}>
                  The employee will no longer be able to apply for WFH. Existing
                  WFH requests and history are kept.
                </Text>
              ) : null}
            </AppCard>
            <EmployeeLeavePoliciesCard
              employeeId={employeeId}
              canManage={canManageWfh}
              isSelf={isSelf}
            />
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <AppButton
              title={busy ? 'Saving…' : 'Save changes'}
              onPress={submit}
              disabled={busy}
            />
            <AppButton
              title="Cancel"
              variant="text"
              onPress={() => navigation.goBack()}
            />
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  center: { justifyContent: 'center' },
  body: { marginTop: spacing.lg, gap: spacing.md },
  card: { gap: spacing.md },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
});
